import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { RestUserService } from '../services/user/api-rest/rest-user.service';
import { firstValueFrom } from 'rxjs';
import { AuthStateService } from '../services/user/auth-state/auth-state.service';

export const authGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const restUserService = inject(RestUserService);
  const authState = inject(AuthStateService);

  const raw = localStorage.getItem('userInfo');

  let isGuest = false;
  let isAuthenticated = false;

  if (raw) {
    try {
      const user = JSON.parse(raw);
      isGuest = user?.isGuest === true;
      isAuthenticated = user?.isAuthenticated === true;
    } catch {
      isGuest = false;
      isAuthenticated = false;
    }
  }

  if (!isAuthenticated || !raw) {
    restUserService.clearSession();
    router.navigate(['/login']);
    return false;
  }

  try {
    let auth = authState.getAuth();
    let permissions = authState.getPermissions();

    if (isGuest) {
      auth = await firstValueFrom(restUserService.validateAuth());
      permissions = auth.authorities ?? [];
    } else {
      [auth, permissions] = await Promise.all([
        firstValueFrom(restUserService.validateAuth()),
        firstValueFrom(restUserService.getPermissions()),
      ]);
    }

    const safePermissions = permissions ?? [];

    if (!auth?.verified2Fa) {
      router.navigate(['/2fa-verification']);
      return false;
    }

    const requiredPlatform = route.data['requiresPlatform'] as
      | number[]
      | undefined;
    if (requiredPlatform && !requiredPlatform.includes(auth.platform)) {
      redirectByPlatform(router, auth.platform, safePermissions);
      return false;
    }

    const requiredRoles = route.data['requiresPermission'] as
      | string[]
      | undefined;
    if (
      requiredRoles &&
      !requiredRoles.some((role) => safePermissions.includes(role))
    ) {
      redirectByPlatform(router, auth.platform, safePermissions);
      return false;
    }

    return true;
  } catch (error: any) {
    if (error?.status === 0) {
      console.warn('[AuthGuard] Server unreachable — keeping session');
      return true;
    }
    router.navigate(['/login']);
    return false;
  }
};

function redirectByPlatform(
  router: Router,
  platform: number,
  authorities: string[],
): void {
  switch (platform) {
    case 0:
      router.navigate(['platformdashboard/dasha']);
      break;
    case 1:
      router.navigate(['platformdashboard/dashb']);
      break;
    case 2:
      if (
        authorities.includes('GUEST_REQUEST') &&
        authorities.includes('VIEWREQ')
      ) {
        router.navigate(['corpo/listreq']);
      } else if (
        authorities.includes('GUEST_DEAL') &&
        authorities.includes('VIEWDEL')
      ) {
        router.navigate(['deal/listDeal']);
      } else {
        router.navigate(['platformdashboard/dashc']);
      }
      break;
    default:
      router.navigate(['dashboard']);
  }
}
